import getCountryCodes from '../translations/countryCodes';
import sellerCountryCodes from './sellerCountryCodes.json';

export const SHIPPING_LISTING_FIELD_KEYS = {
  packageWeightGrams: 'packageWeightGrams',
  packageLengthCm: 'packageLengthCm',
  packageWidthCm: 'packageWidthCm',
  packageHeightCm: 'packageHeightCm',
  customsDescription: 'customsDescription',
  customsHsCode: 'customsHsCode',
  customsOriginCountry: 'customsOriginCountry',
};

export const SHIPPING_LISTING_FIELD_IDS = Object.values(SHIPPING_LISTING_FIELD_KEYS);

export const SHIPPING_PROVIDER_SENDCLOUD = 'sendcloud';
export const SHIPPING_PROVIDER_EASYPOST = 'easypost';

// GB and IT intentionally excluded for MVP (private carrier contracts required).
export const SELLER_COUNTRY_CODES = sellerCountryCodes;

export const USD_LISTING_SELLER_COUNTRY_CODES = ['US', 'CA'];

/**
 * Map a shipping-address save/validation error to a translation id.
 *
 * @param {string} [statusText]
 * @returns {string}
 */
export const getShippingAddressSaveErrorMessageId = statusText => {
  if (statusText === 'shipping_address_invalid') {
    return 'ShippingAddressForm.validationFailed';
  }
  if (statusText === 'shipping_provider_timeout') {
    return 'ShippingAddressForm.providerTimeout';
  }
  if (
    statusText === 'shipping_provider_request_failed' ||
    statusText === 'shipping_provider_not_configured'
  ) {
    return 'ShippingAddressForm.providerFailed';
  }
  return 'ShippingAddressForm.saveFailed';
};

export const PRICE_INPUT_PUBLIC_DATA_KEYS = {
  currency: 'priceInputCurrency',
  amount: 'priceInputAmount',
  fxRate: 'priceFxRate',
  fxDate: 'priceFxDate',
  fxSource: 'priceFxSource',
};

/**
 * Resolve seller country from profile (shipping address first, then sellerCountry fallbacks).
 * @param {Object} [profile] - currentUser.attributes.profile
 * @returns {string|null} ISO alpha-2 country code or null
 */
export const getSellerCountryFromProfile = profile => {
  const country =
    profile?.protectedData?.shippingAddress?.country ||
    profile?.protectedData?.sellerCountry ||
    profile?.publicData?.seller_country ||
    null;
  return country ? `${country}`.trim().toUpperCase() : null;
};

/**
 * US/Canada sellers may enter listing prices in USD (converted to marketplace EUR on save).
 * @param {Object} [profile] - currentUser.attributes.profile
 * @returns {boolean}
 */
export const isUsdListingSeller = profile => {
  const country = getSellerCountryFromProfile(profile);
  return !!country && USD_LISTING_SELLER_COUNTRY_CODES.includes(country);
};

/**
 * Clear stored USD price-input metadata from listing publicData.
 * @returns {Object}
 */
export const clearPriceInputPublicData = () => ({
  [PRICE_INPUT_PUBLIC_DATA_KEYS.currency]: null,
  [PRICE_INPUT_PUBLIC_DATA_KEYS.amount]: null,
  [PRICE_INPUT_PUBLIC_DATA_KEYS.fxRate]: null,
  [PRICE_INPUT_PUBLIC_DATA_KEYS.fxDate]: null,
  [PRICE_INPUT_PUBLIC_DATA_KEYS.fxSource]: null,
});

/**
 * Build publicData payload for a USD price input that was converted to settlement currency.
 * @param {Object} params
 * @param {number} params.amount - USD amount in subunits
 * @param {number} params.rate - FX rate used
 * @param {string} params.date - FX rate date
 * @param {string} params.source - FX source label
 * @returns {Object}
 */
export const buildPriceInputPublicData = ({ amount, rate, date, source }) => ({
  [PRICE_INPUT_PUBLIC_DATA_KEYS.currency]: 'USD',
  [PRICE_INPUT_PUBLIC_DATA_KEYS.amount]: amount,
  [PRICE_INPUT_PUBLIC_DATA_KEYS.fxRate]: rate,
  [PRICE_INPUT_PUBLIC_DATA_KEYS.fxDate]: date,
  [PRICE_INPUT_PUBLIC_DATA_KEYS.fxSource]: source,
});

export const SHIPPING_PRESET_SMALL = 'small';
export const SHIPPING_PRESET_MEDIUM = 'medium';
export const SHIPPING_PRESET_LARGE = 'large';

export const SHIPPING_PRESETS = [
  {
    id: SHIPPING_PRESET_SMALL,
    weightGrams: 500,
    lengthCm: 35,
    widthCm: 25,
    heightCm: 10,
  },
  {
    id: SHIPPING_PRESET_MEDIUM,
    weightGrams: 1000,
    lengthCm: 45,
    widthCm: 35,
    heightCm: 20,
  },
  {
    id: SHIPPING_PRESET_LARGE,
    weightGrams: 2000,
    lengthCm: 60,
    widthCm: 40,
    heightCm: 30,
  },
];

const CATEGORY_SHIPPING_PRESETS = {
  underwear: SHIPPING_PRESET_SMALL,
  socks: SHIPPING_PRESET_SMALL,
  swimwear: SHIPPING_PRESET_SMALL,
  't-shirts': SHIPPING_PRESET_SMALL,
  tops: SHIPPING_PRESET_SMALL,
  'tank-tops': SHIPPING_PRESET_SMALL,
  caps: SHIPPING_PRESET_SMALL,
  'small-accessories': SHIPPING_PRESET_SMALL,
  pants: SHIPPING_PRESET_MEDIUM,
  shorts: SHIPPING_PRESET_MEDIUM,
  hoodies: SHIPPING_PRESET_MEDIUM,
  sportswear: SHIPPING_PRESET_MEDIUM,
  shoes: SHIPPING_PRESET_MEDIUM,
  boots: SHIPPING_PRESET_MEDIUM,
  'medium-accessories': SHIPPING_PRESET_MEDIUM,
  jackets: SHIPPING_PRESET_LARGE,
  coats: SHIPPING_PRESET_LARGE,
  'bulky-items': SHIPPING_PRESET_LARGE,
};

export const getDeepestListingCategoryId = publicData =>
  publicData?.categoryLevel3 || publicData?.categoryLevel2 || publicData?.categoryLevel1 || null;

export const getRecommendedShippingPreset = publicData =>
  CATEGORY_SHIPPING_PRESETS[getDeepestListingCategoryId(publicData)] || null;

export const isValidShippingPreset = presetId =>
  SHIPPING_PRESETS.some(preset => preset.id === presetId);

export const isShippingListingField = fieldConfig =>
  SHIPPING_LISTING_FIELD_IDS.includes(fieldConfig?.key);

export const partitionListingFields = listingFields =>
  (listingFields || []).reduce(
    (acc, fieldConfig) => {
      if (isShippingListingField(fieldConfig)) {
        return { ...acc, shippingListingFields: [...acc.shippingListingFields, fieldConfig] };
      }

      return { ...acc, detailsListingFields: [...acc.detailsListingFields, fieldConfig] };
    },
    { detailsListingFields: [], shippingListingFields: [] }
  );

export const isPositiveInteger = value => Number.isInteger(value) && value > 0;

export const isValidHsCode = value => /^\d{6,10}$/.test(`${value || ''}`.trim());

export const getCountryOptions = (locale, allowedCountryCodes = null) => {
  const countryOptions = getCountryCodes(locale);
  const allowedCodes = Array.isArray(allowedCountryCodes)
    ? allowedCountryCodes.map(code => `${code}`.toUpperCase())
    : null;

  return allowedCodes
    ? countryOptions.filter(country => allowedCodes.includes(country.code))
    : countryOptions;
};

export const findCountry = (countryCode, locale = 'en') => {
  const countryOptions = getCountryOptions(locale);
  return countryOptions.find(country => country.code === countryCode) || null;
};

export const isSupportedCountryCode = countryCode => !!findCountry(countryCode);

export const hasShippingOriginAddress = shippingAddress => {
  const { name, phone, street1, city, state, zip, country } = shippingAddress || {};
  return !!(name && phone && street1 && city && state && zip && country);
};

export const normalizeShippingAddress = address => {
  if (!address) {
    return null;
  }

  const street1AutocompleteSearch = address.street1Autocomplete?.search;

  return {
    name: `${address.name || ''}`.trim(),
    phone: `${address.phone || address.phoneNumber || ''}`.trim(),
    street1: `${address.street1 || address.line1 || street1AutocompleteSearch || ''}`.trim(),
    streetNo: `${address.streetNo || ''}`.trim(),
    line2: `${address.line2 || ''}`.trim(),
    city: `${address.city || ''}`.trim(),
    state: `${address.state || ''}`.trim(),
    zip: `${address.zip || address.postalCode || ''}`.trim(),
    country: `${address.country || ''}`.trim().toUpperCase(),
  };
};

export const areShippingAddressesEqual = (address1, address2) => {
  const normalizedAddress1 = normalizeShippingAddress(address1);
  const normalizedAddress2 = normalizeShippingAddress(address2);

  return JSON.stringify(normalizedAddress1) === JSON.stringify(normalizedAddress2);
};

export const getShippingDetailsFromAddress = address => {
  const normalizedAddress = normalizeShippingAddress(address);

  return normalizedAddress?.name && normalizedAddress?.street1 && normalizedAddress?.zip
    ? {
        shippingDetails: {
          name: normalizedAddress.name,
          phoneNumber: normalizedAddress.phone,
          address: {
            city: normalizedAddress.city,
            country: normalizedAddress.country,
            line1: normalizedAddress.street1,
            line2: normalizedAddress.line2,
            postalCode: normalizedAddress.zip,
            state: normalizedAddress.state,
            streetNo: normalizedAddress.streetNo,
          },
        },
      }
    : {};
};

/**
 * Map a normalized shipping address to Stripe billing address form fields.
 *
 * @param {Object} address shipping address (checkout or saved profile format)
 * @returns {Object} billing field values for StripePaymentForm
 */
export const getBillingFieldsFromShippingAddress = address => {
  const normalized = normalizeShippingAddress(address);
  if (!normalized) {
    return {};
  }

  const streetParts = [normalized.street1, normalized.streetNo].filter(Boolean);
  return {
    name: normalized.name,
    addressLine1: streetParts.join(', '),
    addressLine2: normalized.line2,
    postal: normalized.zip,
    city: normalized.city,
    state: normalized.state,
    country: normalized.country,
  };
};

const getShippingMethodGroupPriority = method =>
  method?.deliveryType && method.deliveryType !== 'home' ? 1 : 0;

const getShippingMethodAmount = method => {
  const amount = Number(method?.amount);
  return Number.isFinite(amount) ? amount : Number.MAX_SAFE_INTEGER;
};

export const orderShippingMethods = methods =>
  Array.isArray(methods)
    ? methods
        .map((method, index) => ({ method, index }))
        .sort((a, b) => {
          const groupDiff =
            getShippingMethodGroupPriority(a.method) - getShippingMethodGroupPriority(b.method);
          const amountDiff = getShippingMethodAmount(a.method) - getShippingMethodAmount(b.method);

          return groupDiff || amountDiff || a.index - b.index;
        })
        .map(({ method }) => method)
    : [];

export const getParcelFromPublicData = publicData => {
  const preset = SHIPPING_PRESETS.find(preset => preset.id === publicData?.shippingPreset);
  if (preset) {
    return {
      weightGrams: preset.weightGrams,
      lengthCm: preset.lengthCm,
      widthCm: preset.widthCm,
      heightCm: preset.heightCm,
    };
  }

  return {
    weightGrams: publicData?.packageWeightGrams,
    lengthCm: publicData?.packageLengthCm,
    widthCm: publicData?.packageWidthCm,
    heightCm: publicData?.packageHeightCm,
  };
};

export const hasCompleteParcelData = publicData => {
  return isValidShippingPreset(publicData?.shippingPreset);
};

export const hasCompleteCustomsData = publicData => {
  return !!getRecommendedShippingPreset(publicData);
};

export const getShippingProviderFromCountry = (sellerCountry, sendcloudSellerCountries = []) => {
  if (!sellerCountry) {
    return null;
  }

  const country = sellerCountry.toUpperCase();
  const sendcloudCountries = sendcloudSellerCountries.map(value => value.toUpperCase());

  if (sendcloudCountries.includes(country)) {
    return SHIPPING_PROVIDER_SENDCLOUD;
  }

  if (['US', 'CA'].includes(country)) {
    return SHIPPING_PROVIDER_EASYPOST;
  }

  return null;
};

export const isShippingReady = ({
  listingPublicData,
  sellerShippingAddress,
  sendcloudSellerCountries = [],
}) => {
  const shippingEnabled = !!listingPublicData?.shippingEnabled;
  if (!shippingEnabled) {
    return false;
  }

  const sellerCountry = sellerShippingAddress?.country;
  return !!(
    hasShippingOriginAddress(sellerShippingAddress) &&
    getShippingProviderFromCountry(sellerCountry, sendcloudSellerCountries) &&
    hasCompleteParcelData(listingPublicData) &&
    hasCompleteCustomsData(listingPublicData)
  );
};
